import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography,
  Box,
  Button
} from '@mui/material';
import { Download } from '@mui/icons-material';
import ClickableSKU from './ClickableSKU';
import Sparkline from './Sparkline';

const InlineDataTable = ({ columns, rows, title, showExport = true, sparklines }) => {
  if (!columns || !rows || rows.length === 0) {
    return null;
  }

  const exportToCSV = () => {
    // Create CSV content
    const csvRows = [];
    
    // Add header
    csvRows.push(columns.join(','));
    
    // Add data rows
    rows.forEach(row => {
      const csvRow = row.map(cell => {
        // Handle cells that might contain commas
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      });
      csvRows.push(csvRow.join(','));
    });
    
    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${title || 'data'}_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {(title || showExport) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          {title && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          )}
          {showExport && (
            <Button
              size="small"
              startIcon={<Download />}
              onClick={exportToCSV}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(71, 189, 198, 0.08)'
                }
              }}
            >
              Export CSV
            </Button>
          )}
        </Box>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell 
                  key={idx}
                  sx={{ 
                    fontWeight: 600,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIdx) => (
              <TableRow key={rowIdx} hover>
                {row.map((cell, cellIdx) => {
                  // First column is SKU - make it clickable
                  if (cellIdx === 0 && /SKP-[0-9]+-[A-Z]+/.test(cell)) {
                    return (
                      <TableCell key={cellIdx} sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        <ClickableSKU sku={cell} inline={true} showIcon={false} />
                      </TableCell>
                    );
                  }
                  
                  // Check if this column has sparkline data
                  const columnName = columns[cellIdx];
                  const hasSparkline = sparklines && sparklines[columnName] && sparklines[columnName][rowIdx];
                  
                  // Numeric cells align right
                  const isNumeric = typeof cell === 'number' || 
                                   (typeof cell === 'string' && /^\$?[0-9,]+\.?\d*$/.test(cell.replace('$', '').replace(',', '')));
                  
                  return (
                    <TableCell 
                      key={cellIdx}
                      align={isNumeric ? 'right' : 'left'}
                      sx={{ 
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isNumeric ? 'flex-end' : 'flex-start' }}>
                        {cell}
                        {hasSparkline && sparklines[columnName][rowIdx].length > 0 && (
                          <Sparkline 
                            data={sparklines[columnName][rowIdx]}
                            width={40}
                            height={16}
                            color="#47BDC6"
                          />
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InlineDataTable;

